import Fuse from 'fuse.js';
import { isNil } from 'lodash';
import { BankTransaction, SystemTransaction, Transaction, TransactionState } from 'types/dto';
import {
  SearchFilter,
  SearchMatch,
  SearchMatchMap,
  SearchMatchRange,
  SearchResult,
} from 'types/search';

/**
 * Convert the given Fuse range tuple to a search match range object
 *
 * @param {RangeTuple} range Source Fuse range tuple
 */
const getFuseRangeAsSearchMatchRange = (range: Fuse.RangeTuple): SearchMatchRange => ({
  from: range[0],
  to: range[1],
});

/**
 * Convert the given Fuse result match to a search match object
 *
 * @param {FuseResultMatch} match Source Fuse result match
 */
const getFuseResultAsSearchMatch = <TEntity>(match: Fuse.FuseResultMatch): SearchMatch<TEntity> => {
  // Create the list of match ranges
  const ranges = match.indices.map(getFuseRangeAsSearchMatchRange);

  return {
    field: match.key as keyof TEntity,
    ranges,
  };
};

/**
 * Check if the specified match is for a root Fuse index entry (and not a nested object)
 *
 * @param {SearchMatch} match Source match object
 */
const isRootFieldSearchMatch = <TEntity>(match: SearchMatch<TEntity>) =>
  match.field.toString().indexOf('.') === -1;

/**
 * Extract entities from the specified search result list
 *
 * @param {Array<SearchResult>} results Source list of search results
 */
export const getSearchResultEntities = <TEntity>(results: Array<SearchResult<TEntity>>) =>
  results.map(result => result.entity);

/**
 * Build a map of entity identifiers and their respective search matches
 *
 * @param {Array<SearchResult>>} results List of results to process
 * @param {Function} getEntityId Entity identifier getter
 */
export const getSearchResultMatches = <TEntity>(
  results: Array<SearchResult<TEntity>>,
  getEntityId: (entity: TEntity) => string,
) =>
  results
    .filter(result => result.matches.length > 0)
    .reduce<SearchMatchMap<TEntity>>((acc, result) => {
      const id = getEntityId(result.entity);
      return acc.set(id, result.matches);
    }, new Map());

/**
 * Build a search index for the specified list of transactions
 *
 * @param {Array<string>} fields List of fields to index on each entity
 * @param {Array<Transaction>} transactions List of entities to index
 */
const createTransactionIndex = <TTransaction extends Transaction>(
  fields: Array<keyof TTransaction | string>,
  transactions: Array<TTransaction>,
) =>
  new Fuse<TTransaction>(transactions, {
    findAllMatches: true,
    ignoreFieldNorm: true,
    ignoreLocation: true,
    includeMatches: true,
    keys: fields as Array<string>,
    shouldSort: false,
    threshold: 0.001,
    useExtendedSearch: true,
  });

/**
 * Build a search index for the specified list of bank transactions
 *
 * @param {Array<Transaction>} transactions List of entities to index
 */
export const createBankTransactionIndex = (transactions: Array<BankTransaction>) =>
  createTransactionIndex(
    [
      'amountFormatted',
      'amountString',
      'bankAccount',
      'ref4',
      'statementDescription1',
      'statementDescription2',
      'statementDescription3',
      'reference'
    ],
    transactions,
  );

/**
 * Build a search index for the specified list of system transactions
 *
 * @param {Array<Transaction>} transactions List of entities to index
 */
export const createSystemTransactionIndex = (transactions: Array<SystemTransaction>) =>
  createTransactionIndex(
    [
      'accountCode',
      'paymentLines.fundCode',
      'amountFormatted',
      'amountString',
      'batchCode',
      'narrative',
      'paymentLines.amount',
      'paymentLines.narrative',
      'paymentLines.amountFormatted',
      'paymentLines.ref1',
      'paymentLines.systemDescription1',
      'paymentLines.systemDescription2',
      'paymentLines.systemDescription3',
      'paymentReference',
      'ref1',
      'methodofPayment',
      'userName',
      'tillCode',
       
    ],
    transactions,
  );

/**
 * Create a filter function that only returns transactions with the specified state
 *
 * @param {TransactionState} state Target transaction state
 */
export const createTransactionStateFilter =
  (state?: TransactionState) => (transaction: Transaction) =>
    isNil(state) || transaction.mappedState === state;

/**
 * Retrieve results matching the specified search query from the search index
 *
 * @param {Fuse} index Source index to perform search on
 * @param {string} query Search query
 * @param {Array} fallback List of transactions to use when the query is empty
 * @param {Array} filters List of additional filter functions to apply on top of search results
 */
export const filterSearchIndex = <TEntity>(
  index: Fuse<TEntity>,
  query: string,
  fallback: Array<TEntity>,
  ...filters: Array<SearchFilter<TEntity>>
): Array<SearchResult<TEntity>> => {
  // Wrap fallback results in envelopes containing no matches
  if (!query) {
    return fallback.map(entity => ({ entity, matches: [] }));
  }

  // Convert Fuse results to a list of search result objects
  const results = index.search(query).reduce<Array<SearchResult<TEntity>>>((acc, result) => {
    const { item: entity, matches: results = [] } = result;

    // Convert Fuse result matches to search matches and remove matches of nested objects
    const matches = results.map(getFuseResultAsSearchMatch).filter(isRootFieldSearchMatch);

    return [...acc, { entity, matches }];
  }, []);

  // Return results without applying any custom filters
  if (!filters.length) {
    return results;
  }

  // Apply custom filters on top of the result set
  return results.filter(result => filters.every(filterFn => filterFn(result.entity)));
};
